import PptxGenJS from "pptxgenjs";
import { TemplateData } from "../index"; // Import types from the main template definition
import { getMediaContent, TEXT_OPTIONS } from "../masters/components";

export const addInstructionsSlide = (pres: PptxGenJS, data: TemplateData) => {
  const slide = pres.addSlide({ masterName: "SPLIT_RIGHT_MASTER" });

  slide.addText("Approach and use of this report", { placeholder: "title" });

  slide.addText(
    [
      {
        text: "Approach",
        options: TEXT_OPTIONS.caption,
      },
      {
        text: "Leaders in today’s complex world face challenges like never before. They cannot rely solely on what they know and are known for. They must be able to disrupt themselves and grow. They have to understand themselves deeply and know what to leverage or leave behind to realize their full leadership potential.",
        options: TEXT_OPTIONS.body,
      },
      {
        text: "Grounded in research and decades of senior executive experience, this model provides a developmental pathway to inform decision-making and support ongoing development.",
        options: TEXT_OPTIONS.body,
      },
    ],
    {
      x: 0.46,
      y: 1.5,
      w: 7.96,
      h: 1.5,
    }
  );

  slide.addText(
    [
      {
        text: "Report",
        options: TEXT_OPTIONS.caption,
      },
      {
        text: "This report provides a synthesized view of the executive’s characteristics based on the following:",
        options: {
          ...TEXT_OPTIONS.body,
          paraSpaceAfter: 4,
        },
      },
      {
        text: "Context surrounding the role and the business",
        options: TEXT_OPTIONS.bullet,
      },
      {
        text: "Feedback from the executive’s managers, peers, and direct reports",
        options: TEXT_OPTIONS.bullet,
      },
      {
        text: "The executive’s responses to the:",
        options: TEXT_OPTIONS.bullet,
      },
      {
        text: "Leadership Span™ solution powered by Hogan Assessments",
        options: {
          ...TEXT_OPTIONS.bullet,
          indentLevel: 2,
        },
      },
      {
        text: "Occupational Personality Questionnaire (OPQ-32)",
        options: {
          ...TEXT_OPTIONS.bullet,
          indentLevel: 2,
        },
      },
      {
        text: "An in-depth interview with an industry expert and a leadership assessment expert",
        options: TEXT_OPTIONS.bullet,
      },
      {
        text: "Use",
        options: {
          ...TEXT_OPTIONS.caption,
          paraSpaceBefore: 24,
        },
      },
      {
        text: "This document has been prepared specifically by Russell Reynolds Associates in support of the organization’s request for an executive assessment and should not be used for any reason other than that for which it was originally produced. The findings are intended to be used in conjunction with other sources of information about the executive. ",
        options: {
          ...TEXT_OPTIONS.body,
          paraSpaceAfter: 4,
        },
      },

      {
        text: "We recommend this report not be used after a period of two years because people develop and acquire new skills over time.",
        options: {
          ...TEXT_OPTIONS.body,
          paraSpaceAfter: 4,
        },
      },
      {
        text: "This document should only be made available to individuals who have received guidance in the interpretation and use of this report and are directly involved in the deployment or development of this individual.",
        options: {
          ...TEXT_OPTIONS.body,
          paraSpaceAfter: 4,
        },
      },

      {
        text: "Disclaimer",
        options: {
          ...TEXT_OPTIONS.caption,
          paraSpaceBefore: 24,
        },
      },
      {
        text: "Russell Reynolds Associates does not evaluate executives on the basis of age, race, sex, national origin, religion, disability, or other protected characteristics.",
        options: TEXT_OPTIONS.body,
      },
    ],
    {
      x: 8.99,
      y: 1.44,
      w: 6.65,
      h: 6,
      valign: "top",
    }
  );

  slide.addImage({
    path: getMediaContent("ppt/media/potentialchart.png"),
    x: 1.61,
    y: 3.23,
    w: 5.19,
    h: 2.5,
  });

  const borderBottomGrey = [
    { type: "none" },
    { type: "none" },
    { type: "solid", color: "#a9a7a6", pt: 2 },
    { type: "none" },
  ];
  const borderBottomBlue = [
    { type: "none" },
    { type: "none" },
    { type: "solid", color: "#2336a2", pt: 2 },
    { type: "none" },
  ];
  const borderBottomPurple = [
    { type: "none" },
    { type: "none" },
    { type: "solid", color: "#4b0c6b", pt: 2 },
    { type: "none" },
  ];

  const tableData = [
    [
      {
        text: "READINESS — NOW",
        options: {
          bold: true,
          fontSize: 10,
          color: "FFFFFF",
          fill: "#2336a2",
          align: "left",
          colspan: 2,
          valign: "middle",
        },
      },
      { text: "", options: { colspan: 1, border: { type: "none" } } },
      {
        text: "POTENTIAL — FUTURE",
        options: {
          bold: true,
          fontSize: 10,
          color: "FFFFFF",
          fill: "#4b0c6b",
          align: "left",
          colspan: 2,
          valign: "middle",
        },
      },
    ],
    [
      {
        text: "Experience",
        options: {
          bold: true,
          color: "#2336a2",
          fontSize: 10,
          align: "left",
          border: borderBottomGrey,
        },
      },
      {
        text: "Leadership Span™",
        options: {
          bold: true,
          color: "#2336a2",
          fontSize: 10,
          align: "left",
          border: borderBottomGrey,
        },
      },
      { text: "", options: { border: { type: "none" } } },
      {
        text: "Growth Factors",
        options: {
          bold: true,
          color: "#4b0c6b",
          fontSize: 10,
          align: "left",
          border: borderBottomGrey,
        },
      },
      {
        text: "Potential Realization",
        options: {
          bold: true,
          color: "#4b0c6b",
          fontSize: 10,
          align: "left",
          border: borderBottomGrey,
        },
      },
    ],
    [
      {
        text: "Does the executive have critical depth and breadth of experience?",
        options: { border: borderBottomBlue },
      },
      {
        text: "Does the executive have the required range of leadership capabilities given current and future demands?",
        options: { border: borderBottomBlue },
      },
      { text: "", options: { border: { type: "none" } } },
      {
        text: "What is the executive’s capacity to adapt and evolve to meet dynamic shifts in environmental and organizational demands?",
        options: { border: borderBottomPurple },
      },
      {
        text: "Does this executive possess the self-knowledge and clarity of purpose to realize their potential in a sustainable way?",
        options: { border: borderBottomPurple },
      },
    ],
    [
      {
        text: "Relevant experience, skills, track record",
        options: {
          italic: true,
          fontSize: 10,
          align: "left",
          border: borderBottomBlue,
        },
      },
      {
        text: "Leadership competencies and range",
        options: {
          italic: true,
          fontSize: 10,
          align: "left",
          border: borderBottomBlue,
        },
      },
      { text: "", options: { border: { type: "none" } } },
      {
        text: "Curiosity and adaptability; drive and resilience; social intelligence",
        options: {
          italic: true,
          fontSize: 10,
          align: "left",
          border: borderBottomPurple,
        },
      },
      {
        text: "Self-knowledge; values and aspirations; wider impact and legacy",
        options: {
          italic: true,
          fontSize: 10,
          align: "left",
          border: borderBottomPurple,
        },
      },
    ],
  ];

  const tableOptions = {
    x: 0.42,
    y: 6.04,
    w: 8,
    h: 2.1,
    fontSize: 10,
    valign: "center",
    rowH: [0.31, 0.31, 0.91, 0.57],
    colW: [1.87, 1.87, 0.29, 1.99, 1.99],
  } as const;

  slide.addTable(tableData as any, tableOptions as any);
};
